// Gallery status query (IMPLEMENTATION §0.2 R9): over the closed readout window (the last `buckets` whole
// seconds, readout.ts):
//   NO_GUN       no gun mounted;
//   STARVED      idle (could fire, queue empty) for more than starvedIdlePermille of the ready time (the
//                ticks a gun was mounted and working): supply below ~75% of the fire rate at 250‰;
//   GUN-LIMITED  queue full for more than gunLimitedFullPermille of the window;
//   FIRING       otherwise.
// fireRateMilli = shots/s × 1000 the mounted gun can fire; supplyRateMilli = ammo items/s × 1000 that entered
// the queue; income = payout $/s; potential = $/s at the full fire rate with the ammo being fired (the
// window's average payout per shot; the head of the queue or a Round when nothing fired yet).
import { floorDiv, payoutOf } from '../core/num.ts';
import type { Int } from '../core/types.ts';
import type { SimDefs } from '../defs.ts';
import { itemValue } from '../entities.ts';
import type { GalleryB } from '../state.ts';

export type Binding = 'noGun' | 'starved' | 'gunLimited' | 'firing';

export interface GalleryStatus {
  binding: Binding;
  fireRateMilli: Int;
  supplyRateMilli: Int;
  /** $/s over the window. */
  income: Int;
  /** $/s at the full fire rate. */
  potential: Int;
  /** Ticks the window covers (grows to buckets × bucketTicks). */
  windowTicks: Int;
}

function closed(xs: readonly Int[], open: Int): Int {
  let n = 0;
  for (let i = 0; i < xs.length; i++) if (i !== open) n += xs[i] ?? 0;
  return n;
}

export function galleryStatus(d: SimDefs, g: GalleryB): GalleryStatus {
  const cfg = d.economy.galleryReadout;
  const w = g.win;
  const windowTicks = w.n * cfg.bucketTicks;
  const ready = closed(w.ready, w.idx);
  const idle = closed(w.idle, w.idx);
  const full = closed(w.full, w.idx);
  const fired = closed(w.fired, w.idx);
  const supplied = closed(w.supplied, w.idx);
  const paid = closed(w.paid, w.idx);
  const tickHz = d.tickHz;
  const supplyRateMilli = windowTicks > 0 ? floorDiv(supplied * 1000 * tickHz, windowTicks) : 0;
  const income = windowTicks > 0 ? floorDiv(paid * tickHz, windowTicks) : 0;
  if (g.gun === null) {
    return {
      binding: 'noGun',
      fireRateMilli: 0,
      supplyRateMilli,
      income,
      potential: 0,
      windowTicks,
    };
  }
  const gun = d.guns[g.gun.tier - 1];
  if (!gun) throw new RangeError(`no gun tier ${g.gun.tier}`);
  const fireRateMilli = floorDiv(1000 * tickHz, gun.fireTicks);
  let perShot: Int;
  if (fired > 0) perShot = floorDiv(paid, fired);
  else {
    const head = g.queue[0];
    perShot = payoutOf(
      itemValue(d, 'ammo', head !== undefined ? head.tier : d.economy.coldOpen.ammoTier),
      gun.multPermille,
    );
  }
  const potential = floorDiv(fireRateMilli * perShot, 1000);
  let binding: Binding = 'firing';
  if (ready > 0 && idle * 1000 > ready * cfg.starvedIdlePermille) binding = 'starved';
  else if (windowTicks > 0 && full * 1000 > windowTicks * cfg.gunLimitedFullPermille) {
    binding = 'gunLimited';
  }
  return { binding, fireRateMilli, supplyRateMilli, income, potential, windowTicks };
}
